import {
  DeviceLocationPermission,
  LocationPreference,
  OrgUserForClient,
  OrgUserOnAzure,
} from '../../../generated-client/models';

export type OrgRole = 'Admin' | 'Dispatch' | 'Mobile User' | string;

export interface OrgUserParams {
  /** The Azure Id of the object (legacy id). */
  id: string;

  /** The organization the user is currently a part of. */
  org_code: string;

  /** Authentication ID (not the Azure object ID). */
  auth_id: string;

  first_name: string;
  last_name: string;

  /** In E.164 format, e.g. "+11234567890". */
  phone_number: string;

  /** User's location preference. Defaults to "emergency_only". */
  location_preference?: LocationPreference;

  /** Most recent device location permission state. */
  device_location_permission?: DeviceLocationPermission;

  /** Assigned role in the organization. */
  role: OrgRole;

  /** True if this user currently holds the prototype-firmware access flag. */
  is_prototyper?: boolean;

  /** Unix epoch (ms) of last interaction, or null/undefined if never. */
  last_interaction_timestamp?: number | null;

  /** Language code for the user, e.g., "en-US". */
  language_code: string;

  /** Unified config ID assigned to the user. */
  unified_config_id?: string | null;

  /** Will be supplanted by regenerated types in Task 27. */
  pvd_config_id?: string | null;
  emergency_config_id?: string | null;
  already_accepted_pvd_etag?: string | null;
  already_accepted_emergency_etag?: string | null;
  should_force_apply_pvd_config?: boolean;
  should_force_apply_emergency_config?: boolean;

  /** Preferred Azure Id for the org user document. If present, supersedes `id`. */
  org_user_azure_id?: string | null;

  /** ETag of the accepted spec version, if any. */
  already_accepted_spec_etag?: string | null;

  /** Object type discriminator. */
  object_type: string;

  /** Must follow format "org_user_{auth_id}". */
  unique_key: string;
}

/**
 * Represents an organization-linked user and their emergency/contact preferences.
 */
export class OrgUser {
  /** Effective document id (prefers `org_user_azure_id` if provided). */
  id: string;

  org_code: string;
  auth_id: string;
  first_name: string;
  last_name: string;
  phone_number: string;

  /** Defaults to "emergency_only". */
  location_preference: LocationPreference;

  device_location_permission: DeviceLocationPermission;

  /** Admin, Dispatch, or Mobile User */
  role: string;

  /** True if this user currently holds the prototype-firmware access flag. */
  is_prototyper: boolean;

  /** Unix epoch (ms) of last interaction, or null if never. */
  last_interaction_timestamp: number | null | undefined;

  unified_config_id: string | null;
  pvd_config_id: string | null;
  emergency_config_id: string | null;
  already_accepted_pvd_etag: string | null;
  already_accepted_emergency_etag: string | null;
  should_force_apply_pvd_config: boolean;
  should_force_apply_emergency_config: boolean;
  org_user_azure_id: string | null;
  already_accepted_spec_etag: string | null;

  object_type: string;
  unique_key: string;
  language_code: string;
  should_force_apply_config: boolean;

  constructor({
    id,
    org_code,
    auth_id,
    first_name,
    last_name,
    phone_number,
    location_preference = LocationPreference.EmergencyOnly,
    device_location_permission = DeviceLocationPermission.Denied,
    language_code,
    role,
    is_prototyper = false,
    last_interaction_timestamp = null,

    unified_config_id = '',
    pvd_config_id = null,
    emergency_config_id = null,
    already_accepted_pvd_etag = null,
    already_accepted_emergency_etag = null,
    should_force_apply_pvd_config = false,
    should_force_apply_emergency_config = false,
    org_user_azure_id = null,
    already_accepted_spec_etag = null,
    object_type,
    unique_key,
    should_force_apply_config = false,
  }: Omit<
    OrgUserOnAzure & { org_user_azure_id?: string | null | undefined },
    | '_ts'
    | '_etag'
    | 'allow_location_tracking_while_compass_connected'
    | 'emergency_contact_auth_ids'
    | 'always_include_in_emergencies'
    | 'auto_add_contacts'
    | 'org_spec_azure_id'
  >) {
    // Prefer the explicit org_user_azure_id when present; otherwise use legacy id
    this.id = org_user_azure_id || id;

    this.org_code = org_code;
    this.auth_id = auth_id;
    this.first_name = first_name;
    this.last_name = last_name;
    this.phone_number = phone_number;

    this.location_preference = location_preference;
    this.device_location_permission = device_location_permission;

    this.role = role;
    this.is_prototyper = is_prototyper;
    this.last_interaction_timestamp = last_interaction_timestamp;

    this.unified_config_id = unified_config_id;
    this.pvd_config_id = pvd_config_id ?? null;
    this.emergency_config_id = emergency_config_id ?? null;
    this.already_accepted_pvd_etag = already_accepted_pvd_etag ?? null;
    this.already_accepted_emergency_etag = already_accepted_emergency_etag ?? null;
    this.should_force_apply_pvd_config = should_force_apply_pvd_config ?? false;
    this.should_force_apply_emergency_config = should_force_apply_emergency_config ?? false;
    this.org_user_azure_id = org_user_azure_id;
    this.already_accepted_spec_etag = already_accepted_spec_etag;

    this.object_type = object_type;
    this.unique_key = unique_key;
    this.language_code = language_code;
    this.should_force_apply_config = should_force_apply_config;
  }

  static fromDTO(user: OrgUserForClient): OrgUser {
    return new OrgUser({
      ...user,
      org_user_azure_id: user.id,
    });
  }
}
